import { useState, useEffect, useCallback, useMemo } from 'react';
import { AsignacionFormState, ASIGNACION_INITIAL, AsignacionItem } from '../types/matricula.types';
import { AñoEscolar, Grado, Seccion, Curso } from '../types/usuario.types';
import { asignacionService } from '../services/matriculaService';
import { catalogoService } from '../services/usuarioService';

export interface AsignacionError { [field: string]: string }

export const useAsignacion = (docenteId: number, onSuccess: () => void) => {
  const [form, setForm]               = useState<AsignacionFormState>(ASIGNACION_INITIAL);
  const [errors, setErrors]           = useState<AsignacionError>({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading]         = useState(true);
  const [asignacionesActuales, setAsignacionesActuales] = useState<AsignacionItem[]>([]);

  const [añosEscolares, setAñosEscolares] = useState<AñoEscolar[]>([]);
  const [grados, setGrados]               = useState<Grado[]>([]);
  const [secciones, setSecciones]         = useState<Seccion[]>([]);
  const [cursos, setCursos]               = useState<Curso[]>([]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [años, grs, secs, curs, asigs] = await Promise.all([
          catalogoService.getAñosEscolares(),
          catalogoService.getGrados(),
          catalogoService.getSecciones(),
          catalogoService.getCursos(),
          asignacionService.getByDocente(docenteId),
        ]);

        setAñosEscolares(años);
        setGrados(grs);
        setSecciones(secs);
        setCursos(curs);
        setAsignacionesActuales(asigs);

        if (asigs.length > 0) {
          const primera = asigs[0];
          setForm({
            anioEscolarId: String(primera.anioEscolarId),
            gradoId:       String(primera.gradoId),
            seccionId:     String(primera.seccionId),
            cursosIds:     asigs.map(a => a.cursoId),
          });
        } else if (años.length > 0) {
          setForm(prev => ({ ...prev, anioEscolarId: String(años[0].idAnioEscolar) }));
        }
      } catch { /* no bloquear */ }
      finally { setLoading(false); }
    };
    init();
  }, [docenteId]);

  const seccionesFiltradas = useMemo(() => {
    if (!form.gradoId) return secciones;
    return secciones.filter(s => s.gradoId === Number(form.gradoId));
  }, [secciones, form.gradoId]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    if (name === 'gradoId') {
      setForm(prev => ({ ...prev, gradoId: value, seccionId: '', cursosIds: [] }));
    } else if (['anioEscolarId', 'seccionId'].includes(name)) {
      setForm(prev => ({ ...prev, [name]: value, cursosIds: [] }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  }, []);

  const toggleCurso = useCallback((cursoId: number) => {
    setErrors(prev => { const n = { ...prev }; delete n.cursosIds; return n; });
    setForm(prev => ({
      ...prev,
      cursosIds: prev.cursosIds.includes(cursoId)
        ? prev.cursosIds.filter(x => x !== cursoId)
        : [...prev.cursosIds, cursoId],
    }));
  }, []);

  const validate = (): AsignacionError => {
    const e: AsignacionError = {};
    if (!form.anioEscolarId)         e.anioEscolarId = 'Requerido';
    if (!form.gradoId)               e.gradoId       = 'Requerido';
    if (!form.seccionId)             e.seccionId     = 'Requerido';
    if (form.cursosIds.length === 0) e.cursosIds     = 'Seleccione al menos un curso';

    if (!e.cursosIds) {
      const duplicados = form.cursosIds.filter(cursoId =>
        asignacionesActuales.some(a =>
          a.cursoId        === cursoId &&
          a.gradoId        === Number(form.gradoId) &&
          a.seccionId      === Number(form.seccionId) &&
          a.anioEscolarId  === Number(form.anioEscolarId),
        ),
      );
      if (duplicados.length > 0) {
        const nombres = duplicados
          .map(id => cursos.find(c => c.idCurso === id)?.nombre ?? String(id))
          .join(', ');
        e.cursosIds = `Ya asignado en este grado/sección/año: ${nombres}`;
      }
    }

    return e;
  };

  const handleSubmit = useCallback(async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const nuevas = await asignacionService.guardarLote(
        docenteId,
        form.cursosIds,
        Number(form.gradoId),
        Number(form.seccionId),
        Number(form.anioEscolarId),
      );
      setAsignacionesActuales(prev => {
        const nuevosIds = nuevas.map(n => n.idAsignacion);
        return [...prev.filter(a => !nuevosIds.includes(a.idAsignacion)), ...nuevas];
      });
      onSuccess();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar asignación');
    } finally {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, docenteId, asignacionesActuales, cursos]);

  const actualizarOrden = useCallback(async (idAsignacion: number, orden: number) => {
    const asignacion = asignacionesActuales.find(a => a.idAsignacion === idAsignacion);
    if (!asignacion) return;
    try {
      const actualizada = await asignacionService.actualizarOrden(asignacion, orden);
      setAsignacionesActuales(prev =>
        prev.map(a => a.idAsignacion === idAsignacion ? { ...a, orden: actualizada.orden } : a)
      );
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al actualizar el orden');
    }
  }, [asignacionesActuales]);

  const eliminarAsignacion = useCallback(async (idAsignacion: number) => {
    setSubmitError(null);
    try {
      await asignacionService.eliminar(idAsignacion);
      const eliminada = asignacionesActuales.find(a => a.idAsignacion === idAsignacion);
      setAsignacionesActuales(prev => prev.filter(a => a.idAsignacion !== idAsignacion));
      if (eliminada) {
        setForm(prev => ({
          ...prev,
          cursosIds: prev.cursosIds.filter(id => id !== eliminada.cursoId),
        }));
      }
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al eliminar la asignación');
    }
  }, [asignacionesActuales]);

  return {
    form, errors, submitting, submitError, loading,
    asignacionesActuales, añosEscolares, grados, secciones: seccionesFiltradas, cursos,
    handleChange, toggleCurso, handleSubmit, eliminarAsignacion, actualizarOrden,
  };
};
